import React from 'react';
import PropTypes from 'prop-types';
import { Meteor } from 'meteor/meteor';
import classnames from 'classnames';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cloneDeep, filter, find, has, isEmpty, isFunction, map } from 'lodash';

import {
  ELEMENT_TYPES,
  LIBRARY_ICON_TYPES
} from '../../../api/AnnotationTemplates/AnnotationTemplates';

import { timeago, monthDayYearAtTime } from '../../../modules/dates';

import TableButtonCell from '../../atoms/TableButtonCell';
import TableEmptyCell from '../../atoms/TableEmptyCell';
import Loading from '../Loading';

import { useStyles } from '../../../styles/mapStylesCustom';

import * as videoOps from '../../../api/Videos/video-operations';

const CompleteButton = ({ completedColumn, item, itemList, completeItem }) => {
  const buttonStyle = item.done ? 'success' : 'light';
  const iconToDraw = item.done ? 'check-circle' : 'circle';
  const completeItemFunction = () => {
    if (isEmpty(itemList)) completeItem(item._id, item.done);
    else completeItem(itemList, item._id, item.done);
  };
  if (!completedColumn) return null;
  if (!has(item, 'done')) return <TableButtonCell />;
  return (
    <TableButtonCell>
      <Button variant={buttonStyle} onClick={completeItemFunction}>
        <FontAwesomeIcon icon={['far', iconToDraw]} size="lg" />
      </Button>
    </TableButtonCell>
  );
};

CompleteButton.propTypes = {
  completedColumn: PropTypes.bool.isRequired,
  itemList: PropTypes.string.isRequired,
  item: PropTypes.object.isRequired,
  completeItem: PropTypes.func.isRequired
};

const DeleteButton = ({ deleteButton, item, itemList, softDeleteItem }) => {
  const softDeleteItemFuncion = () => {
    if (isEmpty(itemList)) softDeleteItem(item._id);
    else softDeleteItem(itemList, item._id);
  };
  if (!deleteButton) return null;
  return (
    <TableButtonCell>
      <Button variant="outline-danger" onClick={softDeleteItemFuncion} title="Delete">
        <FontAwesomeIcon icon="times" />
      </Button>
    </TableButtonCell>
  );
};

DeleteButton.propTypes = {
  deleteButton: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  softDeleteItem: PropTypes.func.isRequired
};

const DownloadButton = ({ downloadButton, item, itemList, downloadItem }) => {
  const classes = useStyles();
  const downloadItemFunction = () => {
    if (isEmpty(itemList)) downloadItem(item._id);
    else downloadItem(itemList, item._id);
  };
  if (!downloadButton) return null;
  return (
    <TableButtonCell>
      <Button
        variant="outline-dark"
        onClick={downloadItemFunction}
        title="Download"
        className={classes.azulIpsilumButton}
      >
        <FontAwesomeIcon icon="download" />
      </Button>
    </TableButtonCell>
  );
};

DownloadButton.propTypes = {
  downloadButton: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  downloadItem: PropTypes.func.isRequired
};

const DuplicateButton = ({ createdNewItem, duplicateButton, item, itemList, duplicateItem }) => {
  const classes = useStyles();
  const duplicateItemFunction = () => {
    if (isEmpty(itemList)) duplicateItem(item._id);
    else duplicateItem(itemList, item._id);
  };
  if (!duplicateButton) return null;
  return (
    <TableButtonCell>
      <Button
        variant="outline-dark"
        onClick={duplicateItemFunction}
        title="Copy"
        className={classes.azulIpsilumButton}
        disabled={!createdNewItem}
      >
        <FontAwesomeIcon icon={['far', 'copy']} size="lg" />
      </Button>
    </TableButtonCell>
  );
};

DuplicateButton.propTypes = {
  duplicateButton: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  duplicateItem: PropTypes.func.isRequired,
  createdNewItem: PropTypes.bool.isRequired
};

const ExportButton = ({ exportButton, item, itemList, exportItem }) => {
  const exportItemFunction = () => {
    if (isEmpty(itemList)) exportItem(item._id);
    else exportItem(itemList, item._id);
  };
  if (!exportButton) return null;
  return (
    <TableButtonCell>
      <Button variant="outline-dark" onClick={exportItemFunction} title="Export">
        <FontAwesomeIcon icon="download" />
      </Button>
    </TableButtonCell>
  );
};

ExportButton.propTypes = {
  exportButton: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  exportItem: PropTypes.func.isRequired
};

const OpenNewTabButton = ({ customURL, isPublic, item, itemList, match, openNewTabButton }) => {
  const classes = useStyles();
  const audience = videoOps.getAudience(item);
  let path = `${match.url}/${itemList}`;
  if (!isEmpty(customURL)) path = Meteor.absoluteUrl(customURL);
  else if (isEmpty(itemList)) path = match.url;
  else if (isPublic) {
    if (audience === 'private' || audience === 'privateLimited') {
      path = Meteor.absoluteUrl(`sharedPrivate/${itemList}`);
    } else {
      path = Meteor.absoluteUrl(`shared/${itemList}`);
    }
  }

  if (!openNewTabButton) return null;
  return (
    <TableButtonCell>
      <a href={`${path}/${item._id}`} target="_blank" rel="noreferrer noopener">
        <Button variant="outline-dark" title="Open" className={classes.azulIpsilumButton}>
          <FontAwesomeIcon icon={['fa', 'link']} size="lg" />
        </Button>
      </a>
    </TableButtonCell>
  );
};

OpenNewTabButton.propTypes = {
  customURL: PropTypes.string.isRequired,
  isPublic: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  openNewTabButton: PropTypes.bool.isRequired
};

const PreviewButton = ({ item, itemList, previewButton, previewItem }) => {
  const previewItemFunction = () => {
    if (isEmpty(itemList)) previewItem(item._id);
    else previewItem(itemList, item._id);
  };
  if (!previewButton) return null;
  return (
    <TableButtonCell>
      <Button variant="light" onClick={previewItemFunction} title="Preview">
        <FontAwesomeIcon icon="eye" size="lg" />
      </Button>
    </TableButtonCell>
  );
};

PreviewButton.propTypes = {
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  previewButton: PropTypes.bool.isRequired,
  previewItem: PropTypes.func.isRequired
};

const SaveButton = ({ item, itemList, saveButton, saveItem }) => {
  const saveItemFunction = () => {
    if (isEmpty(itemList)) saveItem(item._id);
    else saveItem(itemList, item._id);
  };
  if (!saveButton) return null;
  return (
    <TableButtonCell>
      <Button variant="light" onClick={saveItemFunction} title="Save">
        <FontAwesomeIcon icon="save" size="lg" />
      </Button>
    </TableButtonCell>
  );
};

SaveButton.propTypes = {
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  saveButton: PropTypes.bool.isRequired,
  saveItem: PropTypes.func.isRequired
};

const SelectButton = ({ item, itemList, selectColumn, selectedItems, selectItem }) => {
  const buttonStyle = selectedItems.has(item._id) ? 'success' : 'light';
  const iconToDraw = selectedItems.has(item._id) ? 'check-circle' : 'circle';
  const selectItemFunction = () => {
    if (isEmpty(itemList)) selectItem(item._id);
    else selectItem(itemList, item._id);
  };
  if (!selectColumn) return null;
  return (
    <TableButtonCell>
      <Button variant={buttonStyle} onClick={selectItemFunction}>
        <FontAwesomeIcon icon={['far', iconToDraw]} size="lg" />
      </Button>
    </TableButtonCell>
  );
};

SelectButton.propTypes = {
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  selectColumn: PropTypes.bool.isRequired,
  selectedItems: PropTypes.object.isRequired,
  selectItem: PropTypes.func.isRequired
};

const IconCell = ({ iconColumn, iconTooltip, item }) => {
  if (!iconColumn || !has(item, 'icon')) return null;
  return (
    <TableButtonCell>
      <OverlayTrigger
        placement="right"
        delay={{ show: 250, hide: 400 }}
        overlay={props =>
          iconTooltip && (
            <Tooltip id="button-tooltip" {...props}>
              {item.name}
            </Tooltip>
          )
        }
      >
        <>
          {item.libraryIcon === LIBRARY_ICON_TYPES.SVG ? (
            <img
              src={item.iconSvg}
              alt={item.icon}
              style={{ marginRight: '5px', width: '30px', height: '30px' }}
            />
          ) : (
            <FontAwesomeIcon icon={item.icon} size="lg" />
          )}
        </>
      </OverlayTrigger>
    </TableButtonCell>
  );
};

IconCell.propTypes = {
  iconColumn: PropTypes.bool.isRequired,
  iconTooltip: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired
};

const NameDateCells = ({
  additionalInfo,
  additionalInfoFields,
  clickAction,
  customURL,
  datesColumns,
  defaultId,
  history,
  isPublic,
  isTemplateElementList,
  isVideoList,
  item,
  itemList,
  match,
  handleSelectRow,
  showTextElement
}) => {
  const rowClickAction = () => {
    const audience = videoOps.getAudience(item);
    if (clickAction === 'goto') {
      let path = `${match.url}/${itemList}`;
      if (!isEmpty(customURL)) path = customURL;
      else if (isEmpty(itemList)) path = match.url;
      else if (isPublic) {
        if (audience === 'private' || audience === 'privateLimited') {
          path = `/sharedPrivate/${itemList}`;
        } else {
          path = `/shared/${itemList}`;
        }
      }
      history.push(`${path}/${item._id}`);
    } else if (clickAction === 'select') handleSelectRow(item._id);
    else if (clickAction === 'showTaskItem') handleSelectRow(item);
    else console.error('hi, you are trying to do nothing');
  };
  const textElement = !isEmpty(item.labelName) ? item.labelName : '';
  if (isTemplateElementList)
    return (
      <>
        <td role="gridcell" className="align-middle" onClick={rowClickAction}>
          {find(ELEMENT_TYPES.properties, ['value', item.elementType]).humanValue}{' '}
          {showTextElement && textElement}
        </td>
        <TableEmptyCell />
      </>
    );

  let labelToShow = '';
  if (clickAction !== 'showTaskItem') {
    if (isEmpty(defaultId) || defaultId !== item._id) labelToShow = item.name;
    else
      labelToShow = (
        <>
          {item.name}
          <span className="badge badge-pill badge-info ml-1">active</span>
        </>
      );
  } else labelToShow = item.label;

  return (
    <>
      <td role="gridcell" onClick={rowClickAction} className="align-middle">
        {labelToShow}
      </td>
      {additionalInfo &&
        map(additionalInfoFields, field => (
          <td
            key={field}
            role="gridcell"
            onClick={rowClickAction}
            className="d-none d-sm-table-cell align-middle"
          >
            {isFunction(field) ? field(item) : item[field]}
          </td>
        ))}
      {datesColumns && (
        <>
          <td
            role="gridcell"
            onClick={rowClickAction}
            className="d-none d-sm-table-cell align-middle"
          >
            {timeago(item.updatedAt)}
          </td>
          <td
            role="gridcell"
            onClick={rowClickAction}
            className="d-none d-sm-table-cell align-middle"
          >
            {monthDayYearAtTime(item.createdAt)}
          </td>
        </>
      )}
      {!isEmpty(itemList) && !isVideoList && (
        <td role="gridcell" className="align-middle" onClick={rowClickAction}>
          {itemList}
        </td>
      )}
    </>
  );
};

NameDateCells.defaultProps = {
  showTextElement: false
};

NameDateCells.propTypes = {
  additionalInfo: PropTypes.bool.isRequired,
  additionalInfoFields: PropTypes.array.isRequired,
  clickAction: PropTypes.string.isRequired,
  customURL: PropTypes.string.isRequired,
  datesColumns: PropTypes.bool.isRequired,
  defaultId: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  isPublic: PropTypes.bool.isRequired,
  isTemplateElementList: PropTypes.bool.isRequired,
  isVideoList: PropTypes.bool.isRequired,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string.isRequired,
  match: PropTypes.object.isRequired,
  handleSelectRow: PropTypes.func.isRequired,
  showTextElement: PropTypes.bool
};

const ItemRow = ({
  additionalInfo,
  additionalInfoFields,
  clickAction,
  createdNewItem,
  customURL,
  defaultId,
  deleteButton,
  completedColumn,
  completeItem,
  exportButton,
  exportItem,
  downloadButton,
  downloadItem,
  duplicateButton,
  duplicateItem,
  datesColumns,
  handleSelectRow,
  history,
  hoverItem,
  itemList,
  iconColumn,
  iconTooltip,
  isPublic,
  isTemplateElementList,
  isVideoList,
  item,
  match,
  openNewTabButton,
  selectedItems,
  selectColumn,
  selectItem,
  previewButton,
  previewItem,
  saveButton,
  saveItem,
  selectedRow,
  softDeleteItem,
  showDeleteButton,
  showTextElement
}) => {
  let itemClassName = '';
  if (completedColumn) itemClassName = classnames({ 'table-success': item.done });
  else if (selectedRow === item._id) itemClassName = 'table-success';
  return (
    <tr
      className={itemClassName}
      onMouseEnter={() => hoverItem(item._id)}
      onMouseLeave={() => hoverItem('')}
    >
      <SelectButton
        item={item}
        itemList={itemList}
        selectColumn={selectColumn}
        selectedItems={selectedItems}
        selectItem={selectItem}
      />
      <NameDateCells
        additionalInfo={additionalInfo}
        additionalInfoFields={additionalInfoFields}
        clickAction={clickAction}
        customURL={customURL}
        defaultId={defaultId}
        history={history}
        isPublic={isPublic}
        isTemplateElementList={isTemplateElementList}
        isVideoList={isVideoList}
        item={item}
        itemList={itemList}
        match={match}
        datesColumns={datesColumns}
        selectedRow={selectedRow}
        handleSelectRow={handleSelectRow}
        showTextElement={showTextElement}
      />
      <IconCell iconColumn={iconColumn} iconTooltip={iconTooltip} item={item} />
      <ExportButton
        exportButton={exportButton}
        item={item}
        itemList={itemList}
        exportItem={exportItem}
      />
      <PreviewButton
        item={item}
        itemList={itemList}
        previewButton={previewButton}
        previewItem={previewItem}
      />
      <SaveButton item={item} itemList={itemList} saveButton={saveButton} saveItem={saveItem} />
      <OpenNewTabButton
        customURL={customURL}
        isPublic={isPublic}
        item={item}
        itemList={itemList}
        match={match}
        openNewTabButton={openNewTabButton}
      />
      <DownloadButton
        downloadButton={downloadButton}
        item={item}
        itemList={itemList}
        downloadItem={downloadItem}
      />
      <DuplicateButton
        duplicateButton={duplicateButton}
        item={item}
        itemList={itemList}
        duplicateItem={duplicateItem}
        createdNewItem={createdNewItem}
      />
      <CompleteButton
        completedColumn={completedColumn}
        item={item}
        itemList={itemList}
        completeItem={completeItem}
      />
      {showDeleteButton && (
        <DeleteButton
          deleteButton={deleteButton}
          item={item}
          itemList={itemList}
          softDeleteItem={softDeleteItem}
        />
      )}
    </tr>
  );
};

ItemRow.defaultProps = {
  additionalInfo: false,
  additionalInfoFields: [],
  createdNewItem: true,
  clickAction: 'goto',
  completedColumn: false,
  customURL: '',
  datesColumns: true,
  defaultId: '',
  deleteButton: true,
  downloadButton: false,
  duplicateButton: false,
  exportButton: false,
  history: {},
  iconColumn: false,
  iconTooltip: false,
  isPublic: false,
  isTemplateElementList: false,
  isVideoList: false,
  itemList: '',
  match: {},
  openNewTabButton: false,
  previewButton: false,
  saveButton: false,
  selectColumn: false,
  selectedItems: new Set(),
  selectedRow: '',
  showDeleteButton: true,
  completeItem: () => console.error('i dont have a function to complete anything'),
  downloadItem: () => console.error('i dont have a function to download anything'),
  duplicateItem: () => console.error('i dont have a function to duplicate anything'),
  exportItem: () => console.error('i dont have a function to export anything'),
  handleSelectRow: () => console.error('i dont have a function to select anything'),
  hoverItem: () => console.error('i dont have a function to hover anything'),
  previewItem: () => console.error('i dont have a function to preview anything'),
  saveItem: () => console.error('i dont have a function to save anything'),
  selectItem: () => console.error('i dont have a function to select anything'),
  softDeleteItem: () => console.error('i dont have a function to delete anything'),
  showTextElement: false
};

ItemRow.propTypes = {
  additionalInfo: PropTypes.bool,
  additionalInfoFields: PropTypes.array,
  clickAction: PropTypes.string,
  createdNewItem: PropTypes.bool,
  completedColumn: PropTypes.bool,
  customURL: PropTypes.string,
  datesColumns: PropTypes.bool,
  defaultId: PropTypes.string,
  deleteButton: PropTypes.bool,
  downloadButton: PropTypes.bool,
  duplicateButton: PropTypes.bool,
  exportButton: PropTypes.bool,
  history: PropTypes.object,
  iconColumn: PropTypes.bool,
  iconTooltip: PropTypes.bool,
  isPublic: PropTypes.bool,
  isTemplateElementList: PropTypes.bool,
  isVideoList: PropTypes.bool,
  item: PropTypes.object.isRequired,
  itemList: PropTypes.string,
  match: PropTypes.object,
  openNewTabButton: PropTypes.bool,
  previewButton: PropTypes.bool,
  saveButton: PropTypes.bool,
  selectColumn: PropTypes.bool,
  selectedItems: PropTypes.object,
  selectedRow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showDeleteButton: PropTypes.bool,
  completeItem: PropTypes.func,
  downloadItem: PropTypes.func,
  duplicateItem: PropTypes.func,
  exportItem: PropTypes.func,
  handleSelectRow: PropTypes.func,
  hoverItem: PropTypes.func,
  previewItem: PropTypes.func,
  saveItem: PropTypes.func,
  selectItem: PropTypes.func,
  softDeleteItem: PropTypes.func,
  showTextElement: PropTypes.bool
};

const List = ({
  loading,
  items,
  completedColumn,
  isCompleteItemHidden,
  itemIdentifier,
  ...rest
}) => {
  if (loading) return <Loading />;

  let finalItemList = [];
  let filteredItems = cloneDeep(items);

  if (completedColumn && isCompleteItemHidden)
    filteredItems = filter(filteredItems, item => !item.done);

  finalItemList = map(filteredItems, (item, index) => {
    const itemForRow = cloneDeep(item);
    let itemKey = '';
    let itemID = '';
    switch (itemIdentifier) {
      case 'id':
        itemKey = item._id;
        itemID = item._id;
        break;
      case 'index':
        itemKey = `item-${index}`;
        itemID = `${index}`;
        break;
      default:
        break;
    }
    itemForRow._id = itemID;
    return <ItemRow key={itemKey} item={itemForRow} completedColumn={completedColumn} {...rest} />;
  });
  return <tbody>{finalItemList}</tbody>;
};

List.defaultProps = {
  completedColumn: false,
  isCompleteItemHidden: false,
  itemIdentifier: 'id'
};

List.propTypes = {
  completedColumn: PropTypes.bool,
  isCompleteItemHidden: PropTypes.bool,
  itemIdentifier: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool.isRequired
};

export default List;
